import React, { Component } from 'react';
import ReactDOMServer from 'react-dom/server';
import * as d3 from 'd3';
import * as _ from 'lodash';
import Modal from '../components/Modal.js';
import { injectIntl, FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';

import NavigationButton from '../components/NavigationButton';
import Graph from '../components/Graph';
import SinaPlot from '../SinaPlot.js';

import safeUrl from '../util/safeUrl.js';


import helpers from '../util/helpers';

import '../css/GraphReadingTutorial.css';

class AnswerHandler extends Component {
  render() {
    let pageNum = this.props.pageNum;
    let answerAttempt = this.props.answerAttempt;
    let disabledAnswers = [];
    if (this.props.disabledAnswers) {disabledAnswers=this.props.disabledAnswers}

    let buttons = this.props.answerChoices.map((answer) =>
      <button
        className="answer-button"
        key={answer}
        onClick={() => this.props.handleAnswerClick(pageNum, answer, answerAttempt)}
        disabled={disabledAnswers.includes(answer)}
      >
        <FormattedMessage id={`graphreading.answers.${answer}`} />
      </button>
    );

    return (
      <div className="answer-button-container">
        {buttons}
      </div>
    )
  }
}

class GraphReadingTutorialPage extends Component {
  constructor(props) {
    super(props);
    // this.state.chemical = 'PFOA'
    // this.state.group = 'PFAS'
    this.state = {
      chemical: 'PFOA',
      group: 'PFAS',
      currentPage: 0,
      currentSubPage: "q",
      answers: {},
      correctAnswers: {
        0: ["less", "similar", "more"],
        1: ["c"],
        2: ["a"],
        3: ["similar"],
        4: ["less", "similar"]
      },
      numCorrect: 0,
      attempt: 1,
      p5_graph_expanded_once: false,
      p5_graph_currently_expanded: false,
    }
  }

  componentDidMount() {
    // decide which chemicals to use
    let self = this
    function setChemicals() {
      if (Object.keys(self.props.appState.chemicals).length > 0) {
        let minDetectedThreshold = 0.5 // from 1 = all detected to 0 = none detected
        self.getHighestPercentile(minDetectedThreshold)
      } else {
        setTimeout(setChemicals, 200);
      }
    }
    setChemicals()

    // start timer
    this.mountTime = performance.now()
    this.pageStartTime = performance.now()

  }

  componentDidUpdate() {
    // restart the tutorial if needed
    // only from the first "launch" page
    if (this.props.location.hash != "#tour") {
      if (this.state.attempt !== 1 || this.state.currentPage !== 0 || this.state.currentSubPage !== "q") {
        this.restartTutorial()
      }
    }
  }

  componentWillUnmount() {
    let endTime = performance.now();
    let ellapsedSeconds = (endTime - this.mountTime) / 1000;
    let currentPage = this.getPageName(this.state.currentPage);
    if (!this.tutorialEnd) {this.tutorialEnd = "nativeBrowserControls"}

    helpers.eventLog(`poe:total_time.sec - ${ellapsedSeconds}`);
    helpers.eventLog(`poe:exit_method - ${this.tutorialEnd}`);


  }

  getPageName(pageNum) {
    let currentPage
    switch(pageNum) {
      case 0:
        currentPage = "predict"
        break;
      case 1:
        currentPage = "ABChighest"
        break;
      case 2:
        currentPage = "ABCnotDetected"
        break;
      case 3:
        currentPage = "compareStudy"
        break;
      case 4:
        currentPage = "compareUs"
        break;
      case 5:
        currentPage = "expandGraph"
        break;
      case 6:
        currentPage = "selectSources"
        break;
      case 7:
        currentPage = "selectActions"
        break;
      case 8:
        currentPage = "finalPage"
        break;
      default:
        // code block
        currentPage = "undefined"
    }
    return currentPage
  }


  getHighestPercentile(minDetectedThreshold) {
    let allChemicals = [];
    for (let x in this.props.appState.chemicals) {
      let separatorIndex = x.indexOf("-")
      let group = x.substring(0, separatorIndex)
      let chemical = x.substring(separatorIndex+1, x.length)
      allChemicals.push({
        chemical: chemical,
        group: group,
        groupSingular: this.props.intl.messages[`chemicals.${helpers.convertGroupName(group)}.singular`],
        combinedName: x,
      })
    }

    // get chemical metadata
    let chemicalsWithData = [];
    for (let i = 0; i < allChemicals.length; i++) {
      let chemical = allChemicals[i].chemical
      let group = allChemicals[i].group
      let groupSingular = allChemicals[i].groupSingular
      let combinedName = allChemicals[i].combinedName
      let chemData = this.props.appState.chemicals[combinedName]
      let [meDetected, percentile, value, percentDetected, numNonDetects] = this.getGraphMetaData(chemData)
      let benchmark = this.getBenchmarkDetected(chemical)
      let benchmarkDetected
      if (benchmark) {benchmarkDetected = benchmark.detected}

      let chemObject = {
        chemical: chemical,
        group: group,
        groupSingular: groupSingular,
        percentile: percentile,
        value: value,
        detectedResult: meDetected,
        percentDetected: percentDetected,
        benchmarkDetected: benchmarkDetected,
        benchmark: benchmark,
        numNonDetects: numNonDetects,
      }
      chemicalsWithData.push(chemObject)

    }


    // sort list to prioritize the criteria for choosing chemicals:
    // 1. The chemical has at least 1 non detect
    // 2. >50% of the results are detected
    // 3. The person's result is detected
    // 4. The median is detected
    // 5. There are at least 10 non-detects
    // we would like all five to be true, if not, just 1&2, if not, just 1
    // if none of the above apply (unlikely) return the ones with the most detected
    // TODO: what if the benchmark is not defined? - for now, that is 0th criteria

    function atLeastOneNondetect(x) {
     return x.numNonDetects > 0;
    }
    function greaterThanThresholdDetected(x) {
     return x.percentDetected > minDetectedThreshold;
    }
    function personalResultDetected(x) {
     return x.detectedResult;
    }
    function benchmarkDetected(x) {
     return x.benchmarkDetected;
    }
    function atLeastTenNondetect(x) {
     return x.numNonDetects >= 10;
    }
    function benchmarkExists(x) {
      if (x.benchmark == undefined) {return false}
     return true;
    }

    let criteria = [
      benchmarkExists,
      atLeastOneNondetect,
      greaterThanThresholdDetected,
      personalResultDetected,
      benchmarkDetected,
      atLeastTenNondetect,
    ]

    // get the chemical with all possible criteria met, with the given comparator
    // ("greater" or "less")
    function getChemical(data, comparator) {
      let chemical;
      let attemptingCriteria = criteria.length;
      while (chemical == undefined && attemptingCriteria > 0) {
        // try to filter the data by all available criteria
        let meetsAllCriteria = data;
        for (let i = 0; i < attemptingCriteria; i++) {
          meetsAllCriteria = meetsAllCriteria.filter(x => criteria[i](x))
        }

        // if there are chemicals that meet the criteria
        if (meetsAllCriteria.length > 0) {
          // then sort (according to comparator)
          meetsAllCriteria = meetsAllCriteria.sort(function(a,b){
            if (comparator == "greater") {
              return b.percentile - a.percentile
            } else if (comparator == "less") {
              return a.percentile - b.percentile
            }
            return 0;
          })

          // and return the optimal one
          chemical = meetsAllCriteria[0]
          return chemical;
        }

        // if unsuccessful, use one less criteria next time
        attemptingCriteria -= 1;
      }

      // if none of the criteria can be met,
      // just sort by which have the most detected
      let lastResort = data.sort(function(a,b){
        return b.percentDetected - a.percentDetected;
      })
      chemical = lastResort[0]
      return chemical;
    }

    // get the chemical with the **greatest** percentile that meets all possible criteria
    let chemical_1 = getChemical(chemicalsWithData, "greater");
    // now remove this chemical from the list, so we don't pick it again
    let elementPos = chemicalsWithData.indexOf(chemical_1);
    chemicalsWithData.splice(elementPos,1);

    // get the chemical with the **least** percentile that meets all possible criteria
    let chemical_2 = getChemical(chemicalsWithData, "less")

    helpers.eventLog("poe:chemical1 - " + chemical_1.chemical);
    helpers.eventLog("poe:chemical2 - " + chemical_2.chemical);

    chemical_1.answers = this.setChemicalAnswers(chemical_1)
    chemical_2.answers = this.setChemicalAnswers(chemical_2)

    for (let i = 1; i < 5; i++) {
      helpers.eventLog(`poe:chemical1_correctAnswer_${this.getPageName(i)} - ${chemical_1.answers[i]}`);
      helpers.eventLog(`poe:chemical2_correctAnswer_${this.getPageName(i)} - ${chemical_2.answers[i]}`);
    }

    this.setState({
      chemical_1: chemical_1,
      chemical_2: chemical_2,
      chemical: chemical_1.chemical,
      group: chemical_1.group,
      groupSingular: chemical_1.groupSingular,
      correctAnswers: chemical_1.answers
    })


  }

  getGraphMetaData(data) {
    // sort by values, lowest to highest
    let sortedData = data.sort((function (a, b) {
      if (!a.detected && !b.detected) {return 0}
      if (!a.detected && b.detected) {return -1}
      if (a.detected && !b.detected) {return 1}
      if (a.value > b.value) {return 1}
      if (a.value < b.value) {return -1}
      return 0
    }))

    // in case of multiple results per chemical, return the highest percentile
    let myPercentiles = []
    let numDetected = 0;
    let meDetected = false;
    let value;
    for (let d = 0; d < sortedData.length; d++) {
      // find percentile
      if (sortedData[d].mine) {
        value = sortedData[d].value
        if (!sortedData[d].detected) {
          myPercentiles.push(0)
        } else {
          myPercentiles.push(d/sortedData.length)
        }

        // save if I'm detected
        if (sortedData[d].detected) {meDetected = true}
      }

      // count nondetected
      if (sortedData[d].detected) {numDetected++}
    }
    let percentile = Math.max(...myPercentiles)
    let percentDetected = numDetected / sortedData.length;
    let numNonDetects = sortedData.length - numDetected
    return [meDetected, percentile, value, percentDetected, numNonDetects]
  }

  getChemicalData(chemical, group) : DataType[] {
    if (!chemical) {
      chemical = this.state.chemical
    }
    if (!group) {
      group = this.state.group
    }

    let data = this.props.appState.chemicals[group + "-" + chemical];
    data = data.sort((function (a, b) {
      if (!a.detected && !b.detected) {return 0}
      if (!a.detected && b.detected) {return -1}
      if (a.detected && !b.detected) {return 1}
      if (a.value > b.value) {return 1}
      if (a.value < b.value) {return -1}
      return 0
    }))

    for (let i = 0; i < data.length; i++) {
      if (data[i].detected) {
        data[i].percentile = i/data.length
      } else {
        data[i].percentile = 0
      }
    }

    return data
  }

  getBenchmarkDetected(chemical) {
    for (let i = 0; i < this.props.appState.benchmarks.length; i++) {
      let benchmark = this.props.appState.benchmarks[i]
      if (benchmark.chemical == chemical) {
        return benchmark;
      }
    }
  }

  setChemicalAnswers(chemicalInfo) {
    // calculate the correct answer for your result
    let compareToOthersAnswers = [];
    let percentile = chemicalInfo.percentile;
    if (percentile < 0.3) {
      compareToOthersAnswers.push("less")
    }
    if (percentile > 0.2 && percentile < 0.8) {
      compareToOthersAnswers.push("similar")
    }
    if (percentile > 0.7) {
      compareToOthersAnswers.push("more")
    }

    // calculate the correct answer for the benchmark
    // this depends on the pixels and y-axis, so let's make a reference sinaplot
    let compareToBenchmark;
    if (chemicalInfo.benchmark != undefined) {
      let [yAxis, sizing] = this.getSinaplotData(chemicalInfo)
      let benchmarkPx = yAxis(chemicalInfo.benchmark.value)
      let participantPx = yAxis(chemicalInfo.value)

      // the order of the items here must be less, similar, more
      // to correspond to the keys in the en.json file
      compareToBenchmark = [];
      // range for "less": less than median, as long as the center of the face is
      // not on the line for benchmark (considering line width)
      if (participantPx + sizing.benchmarkLineThickness/2 < benchmarkPx) {
        compareToBenchmark.push("less")
      }
      // range for "similar": ------{( )-|-( )}----
      // where | is the median, and ( ) is the face
      // so the center of the face is +/- 1 face width on either side
      // and the outer edge of tha face is +/- 1.5 face width on either side
      if (Math.abs(benchmarkPx-participantPx) < sizing.faceWidth) {
        compareToBenchmark.push("similar")
      }
      // range for "more": more than median, as long as the center of the face is
      // not on the line for benchmark (considering line width)
      if (participantPx - sizing.benchmarkLineThickness/2 > benchmarkPx) {
        compareToBenchmark.push("more")
      }
    }

    let correctAnswers = {
      0: ["less", "similar", "more"], // there is no wrong answer for the first guess
      1: ["c"], // c is always the highest dot
      2: ["a"], // a is always a non-detect dot
      3: compareToOthersAnswers,
      4: compareToBenchmark
    }

    return correctAnswers;
  }

  getSinaplotData(chemicalInfo) {
    let chemical = chemicalInfo.chemical;
    let benchmark = chemicalInfo.benchmark;
    let group = chemicalInfo.group;

    this.sinaPlot = new SinaPlot(this.refs.tempContainer, this.props.appState.avatar);

		this.sinaPlot.useAvatars = this.props.appState.graphs.useAvatars;
		this.sinaPlot.useParticipantAvatar = this.props.appState.graphs.useParticipantAvatar;
		this.sinaPlot.jitter = this.props.appState.graphs.jitter;
		this.sinaPlot.sina = this.props.appState.graphs.sina;
		this.sinaPlot.jitterAmount = this.props.appState.graphs.jitterAmount;
		// this.sinaPlot.labelOffset = this.props.appState.graphs.labelOffset;
		this.sinaPlot.showVisitBadges = false;//this.showVisitBadges();
		this.sinaPlot.units = "not needed"//units;

		this.sinaPlot.yLimits = this.getYLimits(chemicalInfo.group);
		this.sinaPlot.noDetectText = this.props.intl.messages['nodetecttext_yaxis']; // this.props.noDetectText -- not needed
		this.sinaPlot.tutorialMode = false;
		// this.sinaPlot.tutorialSettings = this.props.tutorialSettings;
		this.sinaPlot.setup();

    let benchmarks = [benchmark];
    if (benchmark == undefined) {benchmarks=[]}
    this.sinaPlot.updateData(this.getChemicalData(chemical, group), benchmarks, this.props.appState.participantId);

    return [
      this.sinaPlot.y,
      {
        faceWidth: this.sinaPlot.participantFaceRadius*2,
        benchmarkLineThickness: this.sinaPlot.benchmarkLineThickness,
      }
    ]


  }

  getDataThisGroup(group) : DataType[] {
    if (!group) {group = this.state.group}
    return this.props.appState.groups[group];
  }

  getDetectedData(group){
    if (!group) {group = this.state.group}
    return _.filter(this.getDataThisGroup(group), { detected: true });
  }

  getYLimits(group){
    if (!group) {group = this.state.group}
    return d3.extent(_.map(this.getDetectedData(group), 'value'));
  }

  getChemicalFullName() {
    const myData = _.filter(this.getDataThisGroup(), { mine: true });
    const medium : string = _.uniq(_.map(myData, 'medium'))[0];

    const myData_Avgs =_.chain(myData)
                .groupBy("chemical")
                .mapValues((v,i) => { return {
                                chemical: v[0].chemical,
                                chemical_full: v[0].chemical_full,
                                value_numvisits: v.length,
                                value_avg: _.meanBy(v, 'value') }})
                .value();

    return myData_Avgs[this.state.chemical].chemical_full
  }

  handleAnswerClick(pageNum, answer, answerAttempt) {
    if (!answerAttempt) {answerAttempt = 1}
    let numCorrect = this.state.numCorrect;

    // store the answer
    let answers = this.state.answers
    if (!answers[`page-${pageNum}`]) {
      answers[`page-${pageNum}`] = {}
    }
    answers[`page-${pageNum}`][`a-${answerAttempt}`] = answer;

    // log the answer
    if (pageNum == 0) {
      // there's only one attempt for the predict page, so no need to log that
      helpers.eventLog(`poe:chemical${this.state.attempt}_page_${this.getPageName(pageNum)} - ${answer}`);
    } else {
      helpers.eventLog(`poe:chemical${this.state.attempt}_page_${this.getPageName(pageNum)}_attempt${answerAttempt} - ${answer}`);
    }

    // decide the next page based on which answer is correct
    let subpage = "a"
    let correctAnswers = this.state.correctAnswers[pageNum]
    if (correctAnswers.includes(answer)) {
      subpage = "a"
      if (pageNum != 0) {
        numCorrect++;
      }
    } else if (answerAttempt == 1) {
      subpage = "q2"
    } else {
      subpage = "a2"
    }

    this.setState({
      answers: answers,
      currentSubPage: subpage,
      numCorrect: numCorrect,
    })

  }

  handleNextClick(pageNum) {
    let answers = this.state.answers
    let nextPage = pageNum+1
    let numCorrect = this.state.numCorrect

    // save answers as needed
    if (pageNum == 6) {
      let checkedAnswers = []
      let checkboxes = document.querySelectorAll('input[type=checkbox]')

      for (let i = 0; i < checkboxes.length; i++) {
        let value = checkboxes[i].value
        let checked = (checkboxes[i].checked ? "checked" : "unchecked")
        if (checked == "checked") {
          checkedAnswers.push(value)
        }
        helpers.eventLog(`poe:chemical${this.state.attempt}_page_${this.getPageName(pageNum)}_source_${value} - ${checked}`);
      }

      let freeAnswer = document.getElementById('free-answer-box').value;

      answers[`page-${pageNum}`] = {}
      answers[`page-${pageNum}`][`a-checked`] = checkedAnswers;
      answers[`page-${pageNum}`][`a-otherAnswer`] = freeAnswer

      helpers.eventLog(`poe:chemical${this.state.attempt}_page_${this.getPageName(pageNum)}_text_box - ${freeAnswer}`);
    }
    if (pageNum == 7) {
      let checkedAnswers = []
      let radioContainers = document.querySelectorAll('.radio-container')

      for (let i = 0; i < radioContainers.length; i++) {
        let allCheckboxes = radioContainers[i].querySelectorAll('input[type=radio]')
        let selected_radio = radioContainers[i].querySelector('input[type=radio]:checked')

        if (selected_radio == null) {
          let name = allCheckboxes[0].name
          helpers.eventLog(`poe:chemical${this.state.attempt}_page_${this.getPageName(pageNum)}_${name} - none_selected`);
        } else {
          let name = selected_radio.name
          let value = selected_radio.value
          helpers.eventLog(`poe:chemical${this.state.attempt}_page_${this.getPageName(pageNum)}_${name} - ${value}`);
        }
      }
    }

    // if there is no benchmark, skip over the benchmark page
    if (pageNum == 3) {
      if (this.state.correctAnswers[4] == undefined) {
        // there is no benchmark here, so skip page
        nextPage = nextPage + 1
        // also add one to the number correct, so the person is not penalized
        numCorrect = numCorrect + 1
      }
    }

    // log how long this page took
    let pageEndTime = performance.now();
    let ellapsedSeconds = (pageEndTime - this.pageStartTime)/1000
    helpers.eventLog(`poe:chemical${this.state.attempt}_page_${this.getPageName(pageNum)}_time.sec - ${ellapsedSeconds}`)
    // restart the page timer
    this.pageStartTime = performance.now()

    this.setState({
      answers: answers,
      currentPage: nextPage,
      currentSubPage: "q",
      numCorrect: numCorrect,
    })
  }

  handleExpandedGraphClick(toggleState) {
    this.setState({
      p5_graph_expanded_once: true,
      p5_graph_currently_expanded: toggleState,
    })
  }

  // TODO - make this work for spanish
  // getCompareWord(word) {
  //   if (word == "similar") {return "to"}
  //   else {return "than"}
  // }
  //
  // amountToLevel(word) {
  //   if (word == "more") {return "higher"}
  //   if (word == "less") {return "lower"}
  //   return word
  // }

  skipTutorial() {
    let currentPage = this.getPageName(this.state.currentPage);
    helpers.eventLog(`poe:lastPageBeforeSkip - ${currentPage}`);
    this.tutorialEnd = "skipTutorialClicked"
    this.props.history.push("/summary");
    window.scrollTo(0, 0);
  }

  endTutorial() {
    helpers.eventLog("poe:last_page_button_clicked - viewAllResultsButtonClicked")
    this.tutorialEnd = "viewAllResultsButtonClicked"
    this.props.history.push("/summary");
    window.scrollTo(0, 0);
  }

  retakeTutorial() {
    helpers.eventLog("poe:last_page_button_clicked - retakeTutorial")
    this.setState({
      chemical: this.state.chemical_2.chemical,
      group: this.state.chemical_2.group,
      groupSingular: this.state.chemical_2.groupSingular,
      currentPage: 0,
      currentSubPage: "q",
      answers: {},
      correctAnswers: this.state.chemical_2.answers,
      numCorrect: 0,
      attempt: 2,
      p5_graph_expanded_once: false,
      p5_graph_currently_expanded: false,
    })
  }

  restartTutorial() {
    this.setState({
      chemical: this.state.chemical_1.chemical,
      group: this.state.chemical_1.group,
      groupSingular: this.state.chemical_1.groupSingular,
      currentPage: 0,
      currentSubPage: "q",
      answers: {},
      correctAnswers: this.state.chemical_1.answers,
      numCorrect: 0,
      attempt: 2,
      p5_graph_expanded_once: false,
      p5_graph_currently_expanded: false,
    })
  }

  getPageContent(pageNum) {
    let pagesWithGraphs = [1]
    let circleRadius = 12;
    let rectWidth = circleRadius*2-10
		let rectHeight = rectWidth*0.7

    let pageContent = (<div></div>)
    let iconPath = safeUrl(`/images/${this.props.intl.messages['chemicals.' + helpers.convertGroupName(this.state.group) + '.icon']}`);
    // const iconPath = safeUrl(`/images/${this.props.intl.messages['chemicals.' + chemicalGroup + '.icon']}`);
    let hexColor = helpers.getIconColor(helpers.convertGroupName(this.state.group), this.props.intl)
    const icon = <span className='group-icon title-icon' style={{
        mask: `url(${iconPath})`,
        WebkitMask: `url(${iconPath}) 0% 0% / cover`,
        display: "inline-block",
        background: hexColor,
        WebkitMaskSize: "cover",
        maskSize: "cover",
        width: 40,
        height: 40,
      }}/>

    let nextButton = (
      <button className="next-button" onClick={() => this.handleNextClick(pageNum)}><FormattedMessage id="graphreading.answers.next" /></button>
    )

    let baseGraph;
    if (this.getDataThisGroup()) {
      baseGraph = (<Graph
        appState={this.props.appState}
        data={this.getChemicalData()}
        chemical={this.state.chemical}
        chemical_full = {this.getChemicalFullName()}
        yLimits={this.getYLimits()}
        chemicalGroup={this.state.group}
        tutorialMode={true}
      />)
    }

    //////////////////////////////////////////////////////////////////// PAGE 0
    if (pageNum == 0) {
      let highlightContent;

      if (this.state.currentSubPage == "q") {
        this.pageStartTime = performance.now()
        highlightContent = (
          <div>
            <p>
              <FormattedMessage
                id={`graphreading.page0.how_much_chemical_was_in_your_sample`}
                values={{chemical: this.state.chemical}} />
            </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {1}
              answerChoices = {["less", "similar", "more"]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      }

      else if (this.state.currentSubPage == "a") {
        let answer = this.state.answers["page-0"]["a-1"]
        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page0.you_guessed_${answer}`}
                values={{chemical:this.state.chemical}} /></p>
            {nextButton}
          </div>
        )
      }

      pageContent = (
        <div className="grt-popup-content">
          <p>
            <FormattedHTMLMessage
              id={`graphreading.page0.youre_about_to_find_out`}
              values={{chemical: this.state.chemical}} />
          </p>
          <p>
            <FormattedMessage
              id={`graphreading.page0.chemical_is_a_type_of`}
              values={{
                chemical: <strong>{this.state.chemical}</strong>,
                group: <strong>{this.state.groupSingular}</strong>,
                icon: <span className='grt-icon' style={{
                    mask: `url(${iconPath})`,
                    WebkitMask: `url(${iconPath}) 0% 0% / cover`,
                    display: "inline-block",
                    background: hexColor,
                    WebkitMaskSize: "cover",
                    maskSize: "cover",
                    width: 15,
                    height: 20,
                  }}/>,
              }}
            />
            <FormattedMessage
              id={`graphreading.page0.${helpers.convertGroupName(this.state.group)}.description`}/>
          </p>

          <div className="highlight-box">
            {highlightContent}
          </div>

        </div>
      )
    } else if (pageNum == 1) {
      ////////////////////////////////////////////////////////////////// PAGE 1
      let highlightContent;

      if (this.state.currentSubPage == "q") {
        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page1.which_woman_had_the_most_chemical`}
                values={{chemical: this.state.chemical}} />
            </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {1}
              answerChoices = {["a", "b", "c"]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a") {
        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
              id={`graphreading.page1.other_women_correct`} />
            </p>
            {nextButton}
          </div>
        )
      } else if (this.state.currentSubPage == "q2") {
        let answer = this.state.answers["page-1"]["a-1"]

        highlightContent = (
          <div>
            <p>
              <FormattedMessage
                id={`graphreading.page1.other_women_try_again`}
                values={{answer: answer.toUpperCase()}} />
            </p>

            <div className="tip-box">
              <p>
                <FormattedHTMLMessage
                  id={`graphreading.page1.other_women_tip`} />
              </p>
            </div>

            <p>
              <FormattedHTMLMessage
                id={`graphreading.page1.which_woman_had_the_most_chemical`}
                values={{chemical: this.state.chemical}} />
            </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {2}
              answerChoices = {["a", "b", "c"]}
              disabledAnswers = {[answer]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a2") {
        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page1.other_women_incorrect`}
                values={{chemical:this.state.chemical}}/>
            </p>
            {nextButton}
          </div>
        )
      }

      pageContent = (
        <div className="grt-popup-content">
          <p>
            <FormattedHTMLMessage id={`graphreading.page1.graph_shows_other_women`} values={{chemical: this.state.chemical}} />
          </p>
          <p>
            <FormattedMessage
              id={`graphreading.page1.each_blue_circle`}
              values={{
                icon:
                  <svg className="legend-item-icon" height={2*circleRadius} width={2*circleRadius}>
					          <circle cx={circleRadius} cy={circleRadius} r={circleRadius-2} stroke='rgb(119, 158, 255)' strokeWidth="3px" fill='transparent' />
                  </svg>
              }}
            />
          </p>

          <div className="tutorial-graph-wrapper">
            {React.cloneElement(
              baseGraph,
              {tutorialSettings: {
                  noToggle: true,
                  noBenchmark: true,
                  noAvatar: true,
                  drawSampleDots: true,
                }
              }
            )}
          </div>

          <div className="highlight-box">
            {highlightContent}
          </div>

        </div>
      )
    } else if (pageNum == 2) {
      ////////////////////////////////////////////////////////////////// PAGE 2
      let highlightContent;

      if (this.state.currentSubPage == "q") {
        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page2.which_woman_was_nondetected`}
                values={{chemical: this.state.chemical}} />
            </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {1}
              answerChoices = {["a", "b", "c"]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a") {
        highlightContent = (
          <div>
            <p><FormattedHTMLMessage id={`graphreading.page2.nondetects_correct`} values={{chemical:this.state.chemical}}/></p>
            {nextButton}
          </div>
        )
      } else if (this.state.currentSubPage == "q2") {
        let answer = this.state.answers["page-2"]["a-1"]

        highlightContent = (
          <div>
            <p><FormattedMessage id={`graphreading.page2.nondetects_try_again`} values={{answer: answer.toUpperCase(), chemical:this.state.chemical}} /></p>

            <div className="tip-box">
              <p>
                <FormattedHTMLMessage
                  id={`graphreading.page2.nondetects_tip`}
                  values={{chemical:this.state.chemical}}/>
              </p>
            </div>

            <p>
              <FormattedHTMLMessage
                id={`graphreading.page2.which_woman_was_nondetected`}
                values={{chemical: this.state.chemical}} />
              </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {2}
              answerChoices = {["a", "b", "c"]}
              disabledAnswers = {[answer]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a2") {
        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page2.nondetects_incorrect`}
                values={{chemical:this.state.chemical}}/>
            </p>
            {nextButton}
          </div>
        )
      }

      pageContent = (
        <div className="grt-popup-content">
          <p>
            <FormattedMessage
              id={`graphreading.page2.each_grey_circle`}
              values={{
                chemical:this.state.chemical,
                icon:
                  <svg className="legend-item-icon" height={2*circleRadius} width={2*circleRadius}>
      					    <circle cx={circleRadius} cy={circleRadius} r={circleRadius-2} stroke='#ccc' strokeWidth="3px" fill='transparent' />
      				    </svg>
              }}
            />
          </p>

          <div className="tutorial-graph-wrapper">
            {React.cloneElement(
              baseGraph,
              {tutorialSettings: {
                  noToggle: true,
                  noBenchmark: true,
                  noAvatar: true,
                  drawSampleDots: true,
                }
              }
            )}
          </div>

          <div className="highlight-box">
            {highlightContent}
          </div>

        </div>
      )
    } else if (pageNum == 3) {
      ////////////////////////////////////////////////////////////////// PAGE 3
      let highlightContent;

      if (this.state.currentSubPage == "q") {
        highlightContent = (
          <div>
            <p>
              <FormattedMessage
                id={`graphreading.page3.your_result_compared_to_others`}
                values={{chemical: this.state.chemical}} />
            </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {1}
              answerChoices = {["less", "similar", "more"]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a") {
        let correctAnswer = this.state.correctAnswers[pageNum]

        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page3.your_result_correct.${correctAnswer}`}
                values={{chemical:this.state.chemical}} />
              </p>
            {nextButton}
          </div>
        )
      } else if (this.state.currentSubPage == "q2") {
        let answer = this.state.answers["page-3"]["a-1"]

        highlightContent = (
          <div>
            <p><FormattedMessage id={`graphreading.page3.your_result_not_quite`}/></p>

            <div className="tip-box">
              <p><FormattedHTMLMessage id={`graphreading.page3.your_result_tip`} /></p>
            </div>

            <p>
              <FormattedHTMLMessage
                id={`graphreading.page3.your_result_compared_to_others`}
                values={{chemical: this.state.chemical}} />
            </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {2}
              answerChoices = {["less", "similar", "more"]}
              disabledAnswers = {[answer]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a2") {
        let correctAnswer = this.state.correctAnswers[pageNum]

        highlightContent = (
          <div>
            <p><FormattedHTMLMessage
              id={`graphreading.page3.your_result_incorrect.${correctAnswer}`}
              values={{chemical: this.state.chemical}} /></p>
            {nextButton}
          </div>
        )
      }

      pageContent = (
        <div className="grt-popup-content">
          <p>
            <FormattedMessage id={`graphreading.page3.getting_your_result`} values={{chemical:this.state.chemical}}/>
            <FormattedMessage
              id={`graphreading.page3.tips_to_lower_exposure`}
              values={{
                chemical:this.state.chemical,
                group: <strong>{this.state.group}</strong>,
                icon: <span className='grt-icon' style={{
                    mask: `url(${iconPath})`,
                    WebkitMask: `url(${iconPath}) 0% 0% / cover`,
                    display: "inline-block",
                    background: hexColor,
                    WebkitMaskSize: "cover",
                    maskSize: "cover",
                    width: 15,
                    height: 20,
                  }}/>,
            }}/>
          </p>

          <p>
            <FormattedMessage
              id={`graphreading.page3.face_is_your_personal_result`}
              values={{
                icon:
                  <svg className="legend-item-icon" height={2*circleRadius} width={2*circleRadius}>
                    <image xlinkHref={safeUrl(`/images/${this.props.appState.avatar}.png`)} width={2*circleRadius} height={2*circleRadius}  />
        						<circle cx={circleRadius} cy={circleRadius} r={circleRadius-1} stroke='#ff5000' strokeWidth="2px" fill='transparent' />
                  </svg>
              }}
            />
          </p>

          <div className="tutorial-graph-wrapper">
            {React.cloneElement(
              baseGraph,
              {tutorialSettings: {
                  noToggle: true,
                  noBenchmark: true,
                  noAvatar: false,
                  drawSampleDots: false,
                }
              }
            )}
          </div>

          <div className="highlight-box">
            {highlightContent}
          </div>

        </div>
      )
    } else if (pageNum == 4) {
      ////////////////////////////////////////////////////////////////// PAGE 4
      let highlightContent;

      if (this.state.currentSubPage == "q") {
        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page4.your_result_compared_to_median`}
                values={{chemical: this.state.chemical}} />
            </p>
            <p className="negative-bottom-margin">
              <FormattedMessage id={`graphreading.page4.my_level_is`} />
            </p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {1}
              answerChoices = {["less", "similar", "more"]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a") {
        let correctAnswer = this.state.correctAnswers[pageNum]

        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
                id={`graphreading.page4.us_median_correct.${correctAnswer}`}
                values={{chemical:this.state.chemical}} />
            </p>
            {nextButton}
          </div>
        )
      } else if (this.state.currentSubPage == "q2") {
        let answer = this.state.answers["page-4"]["a-1"]

        highlightContent = (
          <div>
            <p><FormattedMessage id={`graphreading.page4.us_median_not_quite`}/></p>

            <div className="tip-box">
              <p><FormattedHTMLMessage id={`graphreading.page4.us_median_tip`} /></p>
            </div>

            <p>
              <FormattedHTMLMessage
              id={`graphreading.page4.your_result_compared_to_median`}
              values={{chemical: this.state.chemical}} />
            </p>
            <p className="negative-bottom-margin"><FormattedMessage id={`graphreading.page4.my_level_is`} /></p>
            <AnswerHandler
              pageNum = {pageNum}
              answerAttempt = {2}
              answerChoices = {["less", "similar", "more"]}
              disabledAnswers = {[answer]}
              handleAnswerClick = {this.handleAnswerClick.bind(this)}
            />
          </div>
        )
      } else if (this.state.currentSubPage == "a2") {
        let correctAnswer = this.state.correctAnswers[pageNum]

        highlightContent = (
          <div>
            <p>
              <FormattedHTMLMessage
              id={`graphreading.page4.us_median_incorrect.${correctAnswer}`}
              values={{chemical: this.state.chemical}}/>
            </p>
            {nextButton}
          </div>
        )
      }

      pageContent = (
        <div className="grt-popup-content">

          <p>
            <FormattedMessage
              id={`graphreading.page4.the_flag_icon_shows`}
              values={{
                icon:
                  <svg className="legend-item-icon" height={2*circleRadius} width={2*circleRadius}>
                    <line x1="0" x1={13} y1={12} x2={13} y2={25} stroke="rgb(13 94 143)" strokeWidth="3px" />
                    <image xlinkHref={safeUrl(`/images/usflag.svg`)} x={-2} y={0} width={30} height={20}  />
                    <rect x={3} y={3} width={18} height={14} rx={2} ry={2} stroke='rgb(13 94 143)' strokeWidth="2px" fill='transparent' />
                  </svg>
              }}
            />
          </p>

          <div className="tutorial-graph-wrapper">
            {React.cloneElement(
              baseGraph,
              {tutorialSettings: {
                  noToggle: true,
                  noBenchmark: false,
                  noAvatar: false,
                  drawSampleDots: false,
                }
              }
            )}
          </div>

          <div className="highlight-box">
            {highlightContent}
          </div>

        </div>
      )
    } else if (pageNum == 5) {
      ////////////////////////////////////////////////////////////////// PAGE 5

      let unit;
      if (this.state.group == "PFAS") {
        unit = "ng/mL"
      } else if (this.state.group == "PBDEs") {
        unit = "ng/n lipid"
      }
      unit = "ng/mL" // TODO - this is just a placeholder until we have unit text
      let highlightContent = (
        <div>
          <p><FormattedHTMLMessage id={`graphreading.page5.${unit}`} /></p>
          {nextButton}
        </div>
      )

      pageContent = (
        <div className="grt-popup-content">

          <p>
            <FormattedMessage
              id={`graphreading.page5.click_to_expand`}
              values={{
                chemical: <span className='chemical-with-arrow'>{this.state.chemical}</span>
              }}
            />
          </p>

          <div className="tutorial-graph-wrapper">
            {React.cloneElement(
              baseGraph,
              {tutorialSettings: {
                  noToggle: false,
                  noBenchmark: false,
                  noAvatar: false,
                  drawSampleDots: false,
                  clickExpandedGraph: this.handleExpandedGraphClick.bind(this),
                }
              }
            )}
          </div>

          {this.state.p5_graph_expanded_once &&
            <div className="highlight-box">
              {highlightContent}
            </div>
          }

        </div>
      )
    } else if (pageNum == 6) {
      ////////////////////////////////////////////////////////////////// PAGE 6
      let guessAnswer = this.state.answers["page-0"]["a-1"]
      let correctAnswer = this.state.correctAnswers[3]

      let introText = (
        <p>
          <FormattedHTMLMessage
            id={`graphreading.page6.initial_guess.${guessAnswer}`}
            values={{chemical:this.state.chemical}}/>
          <FormattedHTMLMessage
            id={`graphreading.page6.graph_showed.${correctAnswer}`}
            values={{chemical:this.state.chemical}}/>
        </p>
      )
      let boldText
      if (correctAnswer.includes(guessAnswer)) {
        boldText = (
          <p className="bold-blue">
            <FormattedMessage id="graphreading.page6.you_guessed_correctly" />
          </p>
        )
      } else {
        boldText = (
          <p className="bold-blue">
            <FormattedMessage id="graphreading.page6.many_people_are_surprised" />
          </p>
        )
      }

      let numActions = this.props.intl.messages[`graphreading.page6.${helpers.convertGroupName(this.state.group)}.numSources`];
      // if (this.state.group == "PFAS") {
      //   numActions = 6;
      // }
      // if (this.state.group == "PBDEs") {
      //   numActions = 6;
      // }
      let checkboxes = []
      for (let i = 0; i < numActions; i++) {
        let item = (
          <label key={`source${i}`} className="checkbox-container">
            <input type="checkbox" value={i}/>
            <span className="checkmark"></span>
            <p><FormattedMessage id={`graphreading.page6.${helpers.convertGroupName(this.state.group)}.source${i}`} /></p>
          </label>
        )

        checkboxes.push(item)

      }

      let noneOfTheAbove = (
        <label key={`source-none`} className="checkbox-container">
          <input type="checkbox" value="none"/>
          <span className="checkmark"></span>
          <p><FormattedMessage id={`graphreading.page6.noneOfTheAbove`} /></p>
        </label>
      )
      checkboxes.push(noneOfTheAbove)

      let anotherIdea = (
        <label key={`source-another`} className="checkbox-container">
          <input type="checkbox" value="other"/>
          <span className="checkmark"></span>
          <p><FormattedMessage id={`graphreading.page6.anotherIdea`} /></p>
          <textarea id="free-answer-box" rows="3" cols="50"></textarea>
        </label>
      )
      checkboxes.push(anotherIdea)


      let highlightContent = (
        <div>
          <p className="p6-before-checklist">
            <FormattedMessage
              id="graphreading.page6.ways_people_get_exposed"
              values={{group: this.state.group}} />
          </p>
          {checkboxes}
          {nextButton}
        </div>
      )


      pageContent = (
        <div className="grt-popup-content">

          {introText}
          {boldText}

          <div className="highlight-box">
            {highlightContent}
          </div>

        </div>
      )
    } else if (pageNum == 7) {
      ////////////////////////////////////////////////////////////////// PAGE 7
      let guessAnswer = this.state.answers["page-0"]["a-1"]
      let correctAnswer = this.state.correctAnswers[3]

      let introText = (
        <p>
          <FormattedMessage
            id={`graphreading.page7.these_actions_lower_exposure`}
            values={{group:this.state.group}}/>
        </p>
      )

      let toggleRadio = function(id) {
        if(document.getElementById(id).classList.contains('selected')) {
          // radio button was checked
          document.getElementById(id).checked = false;
          document.getElementById(id).classList.remove('selected')
        } else {
          // first uncheck everything else
          let idMinusEnding = id.substring(0, id.length-2)
          let divs = document.querySelectorAll(`[id^="${idMinusEnding}"]`);

          for (let i = 0; i < divs.length; ++i) {
            divs[i].classList.remove('selected')
          }

          // then check this one
          document.getElementById(id).classList.add('selected')
          document.getElementById(id).checked = true;
        }
      }

      let makeAction = function(id, group) {
        return (
          <div key={id}>
            <p><FormattedMessage id={`graphreading.page7.${group}.${id}`} /></p>
            <div className="answer-button-container actions-radios radio-container">
              <input type="radio" name={id} id={`${id}-1`} value={`i_do_this`} onClick={(e)=>toggleRadio(`${id}-1`)}/>
              <label htmlFor={`${id}-1`}><FormattedMessage id={`graphreading.page7.i_do_this`} /></label>
              <input type="radio" name={id} id={`${id}-2`} value={`i_want_to`} onClick={()=>toggleRadio(`${id}-2`)}/>
              <label htmlFor={`${id}-2`}><FormattedMessage id={`graphreading.page7.i_want_to`} /></label>
              <input type="radio" name={id} id={`${id}-3`} value={`not_now`} onClick={()=>toggleRadio(`${id}-3`)}/>
              <label htmlFor={`${id}-3`}><FormattedMessage id={`graphreading.page7.not_now`} /></label>
            </div>
          </div>
        )
      }

      let numberToLetter = function(num) {
        let char = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.charAt(num);
        return char
      }

      let actions = []
      // add the actions that everyone gets
      let numDefaultActions = this.props.intl.messages[`graphreading.page7.${helpers.convertGroupName(this.state.group)}.default_numActions`];
      if (numDefaultActions) {
        for (let i = 0; i < numDefaultActions; i++) {
          let action  = makeAction(`action_default${numberToLetter(i)}`, helpers.convertGroupName(this.state.group))
          actions.push(action)
        }
      } else {
        let action  = makeAction(`action_default`, helpers.convertGroupName(this.state.group))
        actions.push(action)
      }

      let numActions;
      if (this.state.group == "PFAS") {
        numActions = 6;
      }
      let selectedSources = this.state.answers["page-6"]["a-checked"]
      for (let i = 0; i < selectedSources.length; i++) {
        let sourceNum = selectedSources[i]
        if (!isNaN(sourceNum)) {
          let numActions = this.props.intl.messages[`graphreading.page7.${helpers.convertGroupName(this.state.group)}.source${sourceNum}_numActions`];
          if (numActions) {
            for (let j = 0; j < numActions; j++) {
              let action  = makeAction(`action_${sourceNum}${numberToLetter(j)}`, helpers.convertGroupName(this.state.group))
              actions.push(action)
            }
          } else {
            let action  = makeAction(`action_${sourceNum}`, helpers.convertGroupName(this.state.group))
            actions.push(action)
          }
        }
      }

      if (selectedSources.length == 0 || selectedSources.includes("none")) {
        // add for non selected
        let numNonSelected = this.props.intl.messages[`graphreading.page7.${helpers.convertGroupName(this.state.group)}.none_numActions`];
        if (numNonSelected) {
          for (let i = 0; i < numNonSelected; i++) {
            let action  = makeAction(`action_none${numberToLetter(i)}`, helpers.convertGroupName(this.state.group))
            actions.push(action)
          }
        } else {
          let action  = makeAction(`action_none`, helpers.convertGroupName(this.state.group))
          actions.push(action)
        }
      }

      let highlightContent = (
        <div>
          {actions}
        </div>
      )

      pageContent = (
        <div className="grt-popup-content">

          {introText}

          <div className="highlight-box">
            {highlightContent}
          </div>

          <p>
            <FormattedMessage
              id="graphreading.page7.thanks_for_taking_action"
              values={{
                group: <strong>{this.state.group}</strong>,
                icon: <span className='grt-icon' style={{
                    mask: `url(${iconPath})`,
                    WebkitMask: `url(${iconPath}) 0% 0% / cover`,
                    display: "inline-block",
                    background: hexColor,
                    WebkitMaskSize: "cover",
                    maskSize: "cover",
                    width: 15,
                    height: 20,
                  }}/>,
              }}
            />
          </p>
          {nextButton}

        </div>
      )
    } else if (pageNum == 8) {
      ////////////////////////////////////////////////////////////////// PAGE 8

      let introText;
      let buttons;

      helpers.eventLog(`poe:chemical${this.state.attempt}_numCorrectBothAttempts - ${this.state.numCorrect}`)
      if (this.state.attempt == 1) {
        if (this.state.numCorrect == 4) {
          // first try: all correct
          helpers.eventLog("poe:last_page_high_priority_button - view_all_results")
          introText = (
            <p><FormattedMessage id="graphreading.page8.end_tutorial_all_correct_try1" /></p>
          )
          buttons = (
            <div>
              <button className="grt-pg8 emphasized-button" onClick={this.endTutorial.bind(this)}>
                <FormattedMessage id="graphreading.page8.view_all_results" />
                <span className="next-symbol"/>
              </button>
              <button className="grt-pg8 not-emphasized-button" onClick={this.retakeTutorial.bind(this)}>
                <span className="redo-symbol"/>
                <FormattedMessage id="graphreading.page8.take_the_tour_again_detail" />
              </button>
            </div>
          )

        } else {
          // first try: not all correct
          helpers.eventLog("poe:last_page_high_priority_button - retake_tutorial")
          introText = (
            <p><FormattedMessage id="graphreading.page8.end_tutorial_not_correct_try1" /></p>
          )
          buttons = (
            <div>
              <button className="grt-pg8 emphasized-button" onClick={this.retakeTutorial.bind(this)}>
                <span className="redo-symbol white-mask"/>
                <FormattedMessage id="graphreading.page8.take_the_tour_again" />
              </button>
              <button className="grt-pg8 not-emphasized-button" onClick={this.endTutorial.bind(this)}>
                <FormattedMessage id="graphreading.page8.view_all_results_detail" />
                <span className="next-symbol blue-mask"/>
              </button>
            </div>
          )

        }
      } else {
        if (this.state.numCorrect >= 3) {
          // second try: most correct
          introText = (
            <p><FormattedMessage id="graphreading.page8.end_tutorial_most_correct_try2" /></p>
          )
          buttons = (
            <div>
              <button className="grt-pg8 emphasized-button" onClick={this.endTutorial.bind(this)}>
                <FormattedMessage id="graphreading.page8.view_all_results" />
                <span className="next-symbol"/>
              </button>
            </div>
          )

        } else {
          // second try: not most correct
          introText = (
            <p><FormattedHTMLMessage id="graphreading.page8.end_tutorial_not_correct_try2" /></p>
          )
          buttons = (
            <div>
              <button className="grt-pg8 emphasized-button" onClick={this.endTutorial.bind(this)}>
                <FormattedMessage id="graphreading.page8.view_all_results" />
                <span className="next-symbol"/>
              </button>
            </div>
          )

        }
      }

      pageContent = (
        <div className="grt-popup-content">

          {introText}
          {buttons}

        </div>
      )
    }

    return pageContent
  }

  render() {
    let popup = false;
    if (this.props.location.hash == "#tour") {
      popup = true;
    }


    let homepage = (
      <div className="page graphreading">
        <h1><FormattedMessage id="graphreading.title" defaultMessage="How to read your graphs" /></h1>
        <FormattedHTMLMessage id="graphreading.description" />
        <div ref="tempContainer" style={{position: "absolute", top: 0, left: -9999}}></div>
      </div>
    )

    if (!popup) {
      return(homepage)
    }

    let pageContent = this.getPageContent(this.state.currentPage)

    return (
      <div className="full-page-popup graphreading">
        <button className="skip-tour" onClick={this.skipTutorial.bind(this)}>
          <FormattedMessage id="graphreading.skip_tour" />
        </button>
        {pageContent}
        <div ref="tempContainer" style={{position: "absolute", top: 0, left: -9999}}></div>
      </div>
    );
  }
}

export default injectIntl(observer(GraphReadingTutorialPage));



// WEBPACK FOOTER //
// ./src/pages/GraphReadingTutorialPage.js